import { useState } from 'react';
import { supabase } from '../lib/supabase';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !supabase) return;
    setLoading(true);
    setError(null);

    if (mode === 'signup') {
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      setLoading(false);
      if (authError) {
        const msg = authError.message?.toLowerCase();
        if (msg?.includes('rate') || msg?.includes('too many') || msg?.includes('429')) {
          setError('Too many signup attempts — please try again in a few minutes.');
        } else {
          setError(authError.message);
        }
      } else {
        setConfirmEmail(true);
      }
    } else {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (authError) setError(authError.message);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <img src="/meridian-logo.png" alt="Meridian" className="w-16 h-16 rounded-xl" />
          <span className="text-2xl font-bold tracking-[0.25em] text-text-primary">MERIDIAN</span>
        </div>

        <div
          className="bg-bg-card rounded-2xl p-8"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)' }}
        >
          {confirmEmail ? (
            <>
              <h2 className="text-xl font-semibold text-text-primary mb-2">Check your email</h2>
              <p className="text-sm text-text-secondary mb-6 leading-relaxed">
                We sent a confirmation link to <span className="text-text-primary font-medium">{email}</span>. Click the link to activate your account.
              </p>
              <button
                onClick={() => { setConfirmEmail(false); setMode('signin'); setError(null); }}
                className="w-full py-3 text-sm font-semibold bg-accent text-white rounded-xl hover:bg-accent-hover transition-colors"
              >
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-text-primary mb-1">
                {mode === 'signin' ? 'Sign in' : 'Create account'}
              </h2>
              <p className="text-sm text-text-secondary mb-6">
                {mode === 'signin' ? 'Enter your credentials' : 'Enter your details to get started'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="w-full bg-bg-tertiary border border-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all"
                />

                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full bg-bg-tertiary border border-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-all"
                />

                {error && (
                  <p className="text-xs text-danger">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full py-3 text-sm font-semibold bg-accent text-white rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : mode === 'signin' ? 'Sign in' : 'Create account'}
                </button>
              </form>

              <button
                onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(null); }}
                className="w-full text-sm text-accent hover:text-accent-hover transition-colors mt-5"
              >
                {mode === 'signin' ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
              </button>
            </>
          )}
        </div>

        <p className="text-center text-xs text-text-muted mt-6">
          Your personal operating system
        </p>
      </div>
    </div>
  );
}
